import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Maximize2, Minimize2, Send, Sparkles, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useGlobalChat } from '../../contexts/GlobalChatContext';
import { useLocation } from 'react-router-dom';
import { getEdgeFunctionErrorMessage } from '../../lib/api';

interface Msg { from: 'me' | 'bot'; text: string; at?: string }

const GOLD = 'linear-gradient(135deg,#E8C37C,#C49A45 50%,#9C7729)';
const SUGGESTIONS = ['Como foi a semana?', 'Quem está devendo?', 'Agenda dos próximos dias', 'Como está a Stella?', 'Funil de vendas', 'Criar uma tarefa'];

const hhmm = (iso?: string) => {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
};

// Markdown simples: **negrito** e listas com "- ".
function renderInline(line: string) {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <Fragment key={i}>{p}</Fragment>));
}
function RichText({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <div className="space-y-1.5">
      {lines.map((l, i) => {
        const bullet = /^\s*[-•]\s+/.test(l);
        if (!l.trim()) return null;
        return bullet
          ? <div key={i} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" /><span>{renderInline(l.replace(/^\s*[-•]\s+/, ''))}</span></div>
          : <p key={i}>{renderInline(l)}</p>;
      })}
    </div>
  );
}

function Avatar({ size = 32 }: { size?: number }) {
  return (
    <span className="relative inline-flex items-center justify-center rounded-full shrink-0 text-white shadow-md" style={{ width: size, height: size, background: GOLD }}>
      <Sparkles size={Math.round(size * 0.5)} />
    </span>
  );
}

// Chat interno com o Assistente Orion (Íris). Aparece só para quem o módulo libera (hoje, o CEO de empresas com o módulo contratado).
export function AssistantChat() {
  const { user } = useAuth();
  const { activeChatLead } = useGlobalChat();
  const location = useLocation();
  // Chat de WhatsApp do lead aberto (canto inferior direito): a Íris abre ao lado dele, nunca por cima.
  const teamChatOn = !!activeChatLead && !location.pathname.startsWith('/inbox');
  const [available, setAvailable] = useState(false);
  const [persona, setPersona] = useState('Íris');
  const [open, setOpen] = useState(false);
  const [wide, setWide] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [loadedHistory, setLoadedHistory] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const call = useCallback(async (fn: string, body: Record<string, unknown>) => {
    if (!supabase) throw new Error('Sem conexão com o servidor.');
    const { data, error: err } = await supabase.functions.invoke(fn, { body });
    if (err) throw new Error(await getEdgeFunctionErrorMessage(err));
    return data;
  }, []);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const st = await call('assistant-auth', { action: 'status' });
        if (!alive || !st?.module_enabled || !st?.allowed) return;
        setPersona(st.persona || 'Íris');
        setAvailable(true);
      } catch { /* sem módulo: o botão não aparece */ }
    })();
    return () => { alive = false; };
  }, [user, call]);

  useEffect(() => {
    if (!open || !available || loadedHistory) return;
    (async () => {
      try {
        const h = await call('assistant-gateway', { action: 'history' });
        setMsgs((h?.messages ?? []) as Msg[]);
        setLoadedHistory(true);
      } catch (e) { setError(e instanceof Error ? e.message : 'Não consegui abrir o chat.'); }
    })();
  }, [open, available, loadedHistory, call]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [msgs, busy, open, wide]);
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 150); }, [open]);
  useEffect(() => {
    const el = inputRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = `${Math.min(el.scrollHeight, 120)}px`; }
  }, [text]);

  if (!available) return null;
  const firstName = String(user?.name ?? '').trim().split(/\s+/)[0];
  const hasConversation = msgs.length > 0;

  const send = async (override?: string) => {
    const t = (override ?? text).trim();
    if (!t || busy) return;
    setText(''); setError(''); setBusy(true); setPending(false);
    setMsgs((m) => [...m, { from: 'me', text: t, at: new Date().toISOString() }]);
    try {
      const r = await call('assistant-gateway', { action: 'chat', text: t });
      const replies: string[] = r?.replies ?? [];
      setPending(!!r?.pending);
      setMsgs((m) => [...m, ...replies.map((x) => ({ from: 'bot' as const, text: x, at: new Date().toISOString() }))]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não consegui responder agora. Tenta de novo?');
    } finally { setBusy(false); }
  };

  return (
    <>
      <style>{`
        @keyframes iris-ring { 0% { transform: scale(1); opacity: .55 } 100% { transform: scale(1.75); opacity: 0 } }
        @keyframes iris-dot { 0%, 80%, 100% { transform: translateY(0); opacity: .35 } 40% { transform: translateY(-4px); opacity: 1 } }
        @keyframes iris-in { from { opacity: 0; transform: translateY(14px) scale(.97) } to { opacity: 1; transform: none } }
        @keyframes iris-msg { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: none } }
      `}</style>

      {!open && (
        <button
          type="button" onClick={() => setOpen(true)} aria-label={`Abrir o chat com a ${persona}`}
          className={`group fixed z-40 print:hidden ${teamChatOn ? 'bottom-6 right-6 hidden lg:block lg:right-[27rem]' : 'bottom-24 right-6'}`}
        >
          <span className="absolute inset-0 rounded-full" style={{ background: GOLD, animation: 'iris-ring 2.6s ease-out infinite' }} />
          <span
            className="relative flex h-16 w-16 items-center justify-center rounded-full text-white shadow-xl shadow-amber-700/30 ring-2 ring-white/70 transition-transform duration-200 group-hover:scale-110 group-active:scale-95"
            style={{ background: GOLD }}
          >
            <Sparkles size={28} />
            <span className="absolute -right-0.5 -top-0.5 h-4 w-4 rounded-full bg-emerald-500 ring-2 ring-white" />
          </span>
          <span className="pointer-events-none absolute right-[4.75rem] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-xl bg-stone-900 px-3 py-2 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
            Pergunte para a {persona}
          </span>
        </button>
      )}

      {open && (
        <div
          className={`fixed z-50 flex flex-col overflow-hidden bg-stone-50 dark:bg-gray-900 shadow-2xl ring-1 ring-black/10 dark:ring-white/10 print:hidden inset-0 lg:inset-auto lg:bottom-6 lg:rounded-3xl ${teamChatOn ? 'lg:right-[27rem]' : 'lg:right-6'} ${wide ? 'lg:h-[88vh] lg:w-[760px] lg:max-w-[calc(100vw-30rem)]' : 'lg:h-[680px] lg:max-h-[88vh] lg:w-[460px]'}`}
          style={{ animation: 'iris-in .22s ease-out' }}
        >
          <div className="relative flex items-center gap-3 bg-stone-900 px-5 py-4 text-white">
            <div className="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full" style={{ background: 'radial-gradient(circle,rgba(196,154,69,.35),rgba(196,154,69,0) 65%)' }} />
            <Avatar size={44} />
            <div className="relative min-w-0 flex-1">
              <div className="flex items-center gap-2 text-base font-bold leading-tight">{persona}<span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300">Assistente</span></div>
              <div className="mt-0.5 flex items-center gap-1.5 text-xs text-stone-300"><span className="h-2 w-2 rounded-full bg-emerald-400" /> online · consulta os dados do seu Orion</div>
            </div>
            <button type="button" onClick={() => setWide((w) => !w)} aria-label={wide ? 'Diminuir o chat' : 'Ampliar o chat'} className="relative hidden rounded-lg p-2 text-stone-300 hover:bg-white/10 hover:text-white lg:block">
              {wide ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            <button type="button" onClick={() => setOpen(false)} aria-label="Fechar o chat" className="relative rounded-lg p-2 text-stone-300 hover:bg-white/10 hover:text-white"><X size={20} /></button>
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-5 text-[15px] leading-relaxed sm:px-5">
            {!hasConversation && (
              <div className="flex flex-col items-center px-2 pt-6 text-center" style={{ animation: 'iris-msg .3s ease-out' }}>
                <Avatar size={64} />
                <h3 className="mt-4 text-xl font-bold text-stone-900 dark:text-white">Oi{firstName ? `, ${firstName}` : ''}! Eu sou a {persona} 😊</h3>
                <p className="mt-2 max-w-sm text-sm text-stone-600 dark:text-gray-300">Sou a sua mão direita no Orion. Pergunte do seu jeito, que eu busco os números pra você.</p>
              </div>
            )}

            {msgs.map((m, i) => (
              <div key={i} className={`flex items-end gap-2 ${m.from === 'me' ? 'justify-end' : 'justify-start'}`} style={{ animation: 'iris-msg .25s ease-out' }}>
                {m.from === 'bot' && <Avatar size={30} />}
                <div className={`max-w-[84%] ${m.from === 'me' ? 'items-end' : 'items-start'} flex flex-col`}>
                  <div
                    className={`px-4 py-2.5 shadow-sm ${m.from === 'me' ? 'rounded-2xl rounded-br-md text-white' : 'rounded-2xl rounded-bl-md border border-stone-200 bg-white text-stone-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100'}`}
                    style={m.from === 'me' ? { background: GOLD } : undefined}
                  >
                    <RichText text={m.text} />
                  </div>
                  <span className="mt-1 px-1 text-[10px] text-stone-400">{hhmm(m.at)}</span>
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex items-end gap-2">
                <Avatar size={30} />
                <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md border border-stone-200 bg-white px-4 py-3.5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  {[0, 1, 2].map((d) => <span key={d} className="h-2 w-2 rounded-full bg-amber-500" style={{ animation: `iris-dot 1.2s ease-in-out ${d * 0.16}s infinite` }} />)}
                </div>
              </div>
            )}
            {pending && !busy && (
              <div className="flex gap-2 pl-10">
                <button type="button" onClick={() => void send('sim')} className="rounded-full px-4 py-2 text-sm font-semibold text-white shadow-md" style={{ background: GOLD }}>Confirmar ✅</button>
                <button type="button" onClick={() => void send('não')} className="rounded-full border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">Cancelar</button>
              </div>
            )}
            {error && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">{error}</p>}
            <div ref={endRef} />
          </div>

          <div className="border-t border-stone-200 bg-white px-4 pb-3 pt-3 dark:border-gray-700 dark:bg-gray-800 sm:px-5">
            {!busy && (
              <div className="-mx-1 mb-3 flex gap-2 overflow-x-auto px-1 pb-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => void send(s)} className="shrink-0 rounded-full border border-amber-300/70 bg-amber-50 px-3.5 py-1.5 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
                    {s}
                  </button>
                ))}
              </div>
            )}
            <form onSubmit={(e) => { e.preventDefault(); void send(); }} className="flex items-end gap-2 rounded-2xl border border-stone-300 bg-stone-50 p-1.5 focus-within:border-amber-500 focus-within:ring-2 focus-within:ring-amber-500/20 dark:border-gray-600 dark:bg-gray-700">
              <textarea
                ref={inputRef} rows={1} value={text} maxLength={2000} placeholder={`Pergunte para a ${persona}...`}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
                className="max-h-[120px] flex-1 resize-none bg-transparent px-3 py-2 text-[15px] text-stone-900 outline-none placeholder:text-stone-400 dark:text-white"
              />
              <button type="submit" disabled={busy || !text.trim()} aria-label="Enviar" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-md transition-opacity disabled:opacity-40" style={{ background: GOLD }}>
                <Send size={18} />
              </button>
            </form>
            <p className="mt-2 text-center text-[10px] text-stone-400">A {persona} só grava depois do seu "sim" e você pode desfazer em até 1 hora.</p>
          </div>
        </div>
      )}
    </>
  );
}
